import { useState, useMemo } from 'react';
import { useDistricts } from '../../hooks/useDistricts';
import SearchBar from '../shared/SearchBar';
import FilterChips from '../shared/FilterChips';
import RaceCard from '../shared/RaceCard';
import { LoadingState, ErrorState, EmptyState, RaceCardSkeleton } from '../shared/LoadingStates';
import { exportToCSV } from '../../lib/export';
import { enrichDistricts } from '../../lib/metrics';
import { AdvancedFilterPanel } from '../shared/AdvancedFilterPanel';
import { useFilterState } from '../../hooks/useFilterState';
import { isEnabled } from '../../lib/featureFlags';

export default function RacesTab() {
  const [searchQuery, setSearchQuery] = useState(() => (
    new URLSearchParams(window.location.search).get('district') || ''
  ));
  const [selectedFilters, setSelectedFilters] = useState([]);

  const { filters, updateFilters, resetFilters } = useFilterState();
  const useAdvancedFilters = isEnabled('ADVANCED_FILTERS');

  const { data: races, isLoading, error, refetch } = useDistricts({
    ratings: selectedFilters.length > 0 ? selectedFilters : undefined,
    // Enrich districts with win probability and competitiveness metrics
    select: enrichDistricts
  });

  // Client-side search and advanced filter
  const filteredRaces = useMemo(() => {
    if (!races || !Array.isArray(races)) return [];

    return races.filter(race => {
      if (!race) return false;

      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          race.district_code?.toLowerCase().includes(query) ||
          race.name?.toLowerCase().includes(query) ||
          race.incumbent_name?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      // Advanced filters (if enabled)
      if (useAdvancedFilters && filters) {
        // Confidence range
        if (filters.confidenceRange) {
          const confPercent = (race.model_confidence ?? 0.7) * 100;
          if (confPercent < filters.confidenceRange[0] || confPercent > filters.confidenceRange[1]) {
            return false;
          }
        }

        // Margin range
        if (filters.marginRange) {
          const margin = race.margin_2024 ?? 0;
          if (margin < filters.marginRange[0] || margin > filters.marginRange[1]) {
            return false;
          }
        }

        // Competitiveness range
        if (filters.competitivenessRange && race.competitivenessScore !== undefined) {
          if (race.competitivenessScore < filters.competitivenessRange[0] ||
              race.competitivenessScore > filters.competitivenessRange[1]) {
            return false;
          }
        }

        // Open seats only
        if (filters.openSeatsOnly && !race.is_open_seat) {
          return false;
        }
      }

      return true;
    });
  }, [races, searchQuery, filters, useAdvancedFilters]);

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="h-full overflow-y-auto" id="races-panel" role="tabpanel">
      <div className="sticky top-0 bg-slate-900 z-10 border-b border-slate-700">
        <div className="p-4 space-y-3">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search districts or candidates..."
          />

          {/* Advanced Filter Panel (feature-flagged) */}
          {useAdvancedFilters && (
            <AdvancedFilterPanel
              filters={filters}
              updateFilters={updateFilters}
              resetFilters={resetFilters}
            />
          )}

          {/* Traditional Filter Chips (fallback if advanced filters disabled) */}
          {!useAdvancedFilters && (
            <FilterChips
              selected={selectedFilters}
              onChange={setSelectedFilters}
              options={[
                { value: 'Safe D', label: 'Safe D', color: 'blue' },
                { value: 'Likely D', label: 'Likely D', color: 'blue' },
                { value: 'Lean D', label: 'Lean D', color: 'purple' },
                { value: 'Toss-Up', label: 'Toss-Up', color: 'purple' },
                { value: 'Lean R', label: 'Lean R', color: 'red' },
                { value: 'Likely R', label: 'Likely R', color: 'red' },
                { value: 'Safe R', label: 'Safe R', color: 'red' }
              ]}
            />
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-400">
              {isLoading ? (
                'Loading...'
              ) : (
                <>
                  Showing <strong className="text-white">{filteredRaces?.length || 0}</strong> race{filteredRaces?.length !== 1 ? 's' : ''}
                </>
              )}
            </div>

            {filteredRaces && filteredRaces.length > 0 && (
              <button
                onClick={() => exportToCSV(filteredRaces)}
                className="flex items-center gap-2 px-3 py-2 bg-success text-white rounded-lg text-sm font-semibold hover:bg-success/90 transition-colors"
                aria-label="Export to CSV"
              >
                <span>📥</span> Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Announce results for screen readers */}
        <div role="status" aria-live="polite" className="sr-only">
          Showing {filteredRaces.length} races.
          {useAdvancedFilters && filters.activePreset && ` Preset: ${filters.activePreset}.`}
        </div>

        {isLoading ? (
          <>
            {[1, 2, 3, 4, 5].map(i => (
              <RaceCardSkeleton key={i} />
            ))}
          </>
        ) : filteredRaces && Array.isArray(filteredRaces) && filteredRaces.length > 0 ? (
          filteredRaces.map(race => (
            <RaceCard key={race.id || race.district_code} district={race} />
          ))
        ) : (
          <EmptyState
            icon="🔍"
            title="No results found"
            description="Try adjusting your filters or search query"
            action={
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedFilters([]);
                  if (useAdvancedFilters) {
                    resetFilters();
                  }
                }}
                className="mt-4 px-4 py-2 bg-accent-blue-dark text-white rounded-lg font-semibold hover:bg-accent-blue-dark/90 transition-colors"
              >
                Clear All Filters
              </button>
            }
          />
        )}
      </div>
    </div>
  );
}
